import { useEffect, useRef, useState } from "react"
import { CustomModal } from "components/CustomModal"
import { StarIcon } from "lucide-react"
import { Box, Button, Textarea, Typography } from "@strapi/design-system"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { ActivatedOpinionPopup, opinionService } from "../LeaveOpinionFeatureService"
import { useTranslation } from "react-i18next"

export const LeaveOpinionFeatureModal = ({ type }: { type: ActivatedOpinionPopup }) => {
  const { integrationType } = useAppSelector(selectUserInfo)
  return opinionService.isEnabled(integrationType) ? <LeaveOpinionModal type={type} /> : null
}

const LeaveOpinionModal = ({ type }: { type: ActivatedOpinionPopup }) => {
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [rating, setRating] = useState<number | null>(null)
  const [hoverRating, setHoverRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState("")
  const [message, setMessage] = useState("")
  const wasOpenedThisSession = useRef(false)

  const messageMaps = {
    [ActivatedOpinionPopup.OPINION_AFTER_INITIAL_SETUP]: t("opinion.setupCompleteMessage"),
  }

  useEffect(() => {
    if (wasOpenedThisSession.current) return
    ;(async () => {
      const currentPopup = await opinionService.getCurrentPopup({ userInfo })
      if (currentPopup === type) {
        setMessage(messageMaps[type] || t("opinion.initialMessage"))
        setIsModalOpen(true)
        wasOpenedThisSession.current = true
      }
    })()
  }, [type, userInfo, t])

  const handleClose = async () => {
    opinionService.dismissPopup({ userInfo, popupType: type })
    setIsModalOpen(false)
    resetForm()
  }

  const handleRatingSelect = (selectedRating: number) => {
    setRating(selectedRating)
  }

  const handleSubmit = async () => {
    if (rating === null) return

    opinionService.submitOpinion({
      userInfo,
      popupType: type,
      rating,
      description: rating <= 3 ? feedback : undefined,
    })

    if (rating >= 4)
      window.open("https://www.tiendanube.com/tienda-aplicaciones-nube/banuba?q=tint", "_blank")

    setIsModalOpen(false)
    resetForm()
  }

  const resetForm = () => {
    setRating(null)
    setHoverRating(null)
    setFeedback("")
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      title={t("opinion.modalTitle")}
      onClose={handleClose}
      closeOnBackgroundClick={false}
    >
      <Box padding={4} className="flex flex-col gap-4">
        <Box paddingBottom={4} className="flex flex-col items-center gap-4" textAlign="center">
          <Typography fontWeight="bold" variant="beta" as="h2">
            {rating === null
              ? t("opinion.initialQuestion")
              : rating >= 4
              ? t("opinion.positiveResponse")
              : t("opinion.negativeResponse")}
          </Typography>
          <Box paddingTop={2} paddingBottom={4}>
            <Typography textColor="neutral700" style={{ fontSize: "15px" }}>
              {rating === null
                ? message
                : rating >= 4
                ? t("opinion.positivePrompt")
                : t("opinion.negativePrompt")}
            </Typography>
          </Box>
          <Box className={"flex flex-row"}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleRatingSelect(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(null)}
                className="!px-1"
              >
                <StarIcon
                  className={`h-8 w-8 ${
                    (hoverRating || rating) && star <= (hoverRating || rating)
                      ? "text-yellow-400"
                      : "text-gray-300"
                  }`}
                  fill="currentColor"
                />
              </button>
            ))}
          </Box>
        </Box>

        {rating !== null && rating <= 3 && (
          <Box className={"flex flex-col w-[360px] gap-4"}>
            <Typography variant="delta">{t("opinion.improvementQuestion")}</Typography>
            <Textarea
              id="feedback"
              name="feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t("opinion.feedbackPlaceholder")}
            />
          </Box>
        )}

        <Box display="flex" className="justify-center gap-14">
          {rating !== null && (
            <Button onClick={handleSubmit} disabled={rating <= 3 && feedback.length < 1}>
              {t("opinion.submitButton")}
            </Button>
          )}
        </Box>
      </Box>
    </CustomModal>
  )
}
